import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import Label from 'components/label/Label';
// @mui
import {
  Card,
  Box,
  Table,
  Stack,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableFooter,
  Tooltip,
  Link,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Divider,
  Grid,
  TextField
} from '@mui/material';

import Iconify from '../components/iconify';
import Autocomplete from '@mui/material/Autocomplete';
import useResponsive from '../hooks/useResponsive';
import { useTheme } from '@mui/material/styles';

import FormProvider from 'hooks/form/form-provider';

// components
import Scrollbar from '../components/scrollbar';

import { styled } from '@mui/system';
import { useSnackbar } from '../components/snackbar';

import { LoadingButton } from '@mui/lab';
import CircularProgress from '@mui/material/CircularProgress';
import { Add, Close, Edit } from '@mui/icons-material'
import { getAssociatedCenter, saveB2BProfile } from 'api/users';
import { copyToClipboard } from 'utils/Utils';

import { countries } from '../hooks/form/countries';
import { useAuthDispatch } from 'components/AuthStateProvider';

import { RHFAutocomplete } from '../hooks/form/rhf-autocomplete';

import { useForm, Controller } from 'react-hook-form';

//-----------------------------------------------------------------------
const OverflowTypography = styled(Typography)(() => ({
  maxWidth: 150, // percentage also works
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
}));

export default function ManageB2BUsers() {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const isLg = useResponsive('up', 'md');
  const [selectedData, setSelectedData] = useState(null);
  const [deleteData, setDeleteData] = useState(null);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);


  const indianStates = [
    'Andhra Pradesh',
    'Arunachal Pradesh',
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
    'Andaman and Nicobar Islands',
    'Chandigarh',
    'Dadra and Nagar Haveli',
    'Daman and Diu',
    'Delhi',
    'Lakshadweep',
    'Puducherry',
  ];

  const fetchData = async () => {
    setLoading(true); 
    try {
      const response = await getAssociatedCenter({ userType:'center'});
      setLoading(false);
  
      if (response && response.success) {
        setData(response.data || []); 
      } else {
        enqueueSnackbar(response?.message || "Failed to fetch data", { variant: 'error' });
      }
    } catch (e) {
      console.error(e); 
      setLoading(false); 
      enqueueSnackbar("Unable to fetch B2B users.", { variant: 'error' });
    }
  };
  
  const onDeleteDataClick = (data) => async (e) => {
    try {
      if (deleteData) {
        setLoading(true)
        const response = await saveB2BProfile({ userType:'center', phone_number: data?.phone_number, status: 'deleted' , center_id: data?.api_key })
        if (response.success === true && response.status == 200) {
          setDeleteData(null);
          setLoading(false);
          setData(prevData => [...prevData.filter(item => item?.phone_number !== data?.phone_number)]);
          enqueueSnackbar(response.message || "User deleted successfully.", { variant: 'success' })
        } else if (response.success === false) {
          setDeleteData(null);
          setLoading(false);
          enqueueSnackbar(response.message || "Unable to delete the user.", { variant: 'error' })
        }
      } else {
        enqueueSnackbar("Please select user to delete.", { variant: 'error' })
      }
    } catch (e) {
      console.log(e)
      enqueueSnackbar(e.message || "Unable to delete the user.", { variant: 'error' })
    }
  }

  const defaultValues = Object.keys(data)?.length ? {
    ...{ ...data, ...data?.company_data },
    country_code: data?.country_code?.replace(`+`, ''),
    phone_number: data?.phone_number?.replace(`${data?.country_code || '+91'}`, ''),
    TWELVE_LEAD: data.tests_enabled?.split(',').includes('TWELVE_LEAD'),
    LEAD_TWO: data.tests_enabled?.split(',').includes('LEAD_TWO'),
    SPANDAN_LEGACY: data?.devices_enabled?.split(',').includes('SPANDAN_LEGACY'),
    SPANDAN_NEO: data?.devices_enabled?.split(',').includes('SPANDAN_NEO'),
    SPANDAN_PRO: data?.devices_enabled?.split(',').includes('SPANDAN_PRO'),
    is_active: data?.is_active,
    is_offline_sdk_mode: data?.is_offline_sdk_mode,
    verifier_token: data?.verifier_token,
    usage_limit: data?.usage_limit,
    settings: data?.settings || {
      can_view_data: true,
      can_download_report: true,
      can_request_for_review: true,
    }
  } : {
    country_code: '91',
    phone_number: '',
    name: '',
    email: '',
    business_name: '',
    gst_number: '',
    billing_address: '',
    state: '',
    pin: '',
    code: '',
    settings: {
      can_view_data: true,
      can_download_report: true,
      can_request_for_review: true,
      has_center: ''
    }
  };
  const handleEditClick = (row) => {
    setSelectedData(row);
    setValue("country_code", row.country_code.replace("+", ""));
    setValue("phone_number", row.phone_number.replace(row.country_code, ""));
    setValue("name", row.name);
    setValue("email", row.email);
    setValue("billing_address", row?.center_data?.billing_address);
    setValue("state", row?.center_data?.state);
    setValue("pin", row?.center_data?.pin);
  };
  const methods = useForm({
    defaultValues,  
    mode: "onChange",
  });

  const { control, register, handleSubmit, setValue, reset, formState: { errors, isSubmitting } } = methods;

  const onSubmit = handleSubmit(async (formData) => {
    try {
      formData.country_code = `+${formData?.country_code}`;
      formData.phone_number = `${formData?.country_code}${formData?.phone_number}`;
      let enabledDevices = Object.keys(formData)
        .filter(key => formData[key] && ['SPANDAN_PRO', 'SPANDAN_NEO', 'SPANDAN_LEGACY'].includes(key))
        .join(',');
      let testsEnabled = Object.keys(formData)
        .filter(key => formData[key] && ['TWELVE_LEAD', 'LEAD_TWO'].includes(key))
        .join(',');

      formData.devices_enabled = enabledDevices;
      formData.tests_enabled = testsEnabled;
      formData.userType = 'center';
      if (selectedData) formData.center_id = selectedData?.id;
      const response = await saveB2BProfile(formData);

      if (response.success === true && response.status == 200) {
        enqueueSnackbar(response.message, { variant: 'success' });
        reset();
        setSelectedData(null);
        fetchData();
      } else if (response.success === false) {
        enqueueSnackbar("Unable to save the data.", { variant: 'error' });
      }
    } catch (e) {
      console.log(e);
      enqueueSnackbar("Unable to save the data.", { variant: 'error' });
    }
  });
  return (
    <>
      <Helmet>
        <title> Manage Centers | Sunfox Experts</title>
      </Helmet>
      <Container sx={{ marginTop: (isLg ? 0 : 1) }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h4" gutterBottom>
            Manage Centers { data?.length ? ` (${data.length})` : null }
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={() => { setSelectedData({}); }}>
              <Add sx={{ mr: 1 }} />
              Add Center
            </Button>
          </Stack>
        </Stack>
        <Card>
          <Box>
            <Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '30px', alignItems: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Scrollbar>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 150 }}>Center Name</TableCell>
                        {/* <TableCell sx={{ minWidth: 150 }}>Center Number</TableCell> */}
                        <TableCell sx={{ minWidth: 100 }}>Center Phone No.</TableCell>
                        <TableCell>Email Id</TableCell>
                        {/* <TableCell>Center Id</TableCell> */}
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    {data.length ? (
                      <TableBody>
                        {data?.map((row, index) => {
                          return (
                            <TableRow hover key={index} tabIndex={-1}>
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                  <Tooltip title={row?.name}>
                                    <OverflowTypography variant="subtitle2" noWrap>
                                      {row?.name}
                                    </OverflowTypography>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <OverflowTypography maxWidth={150} variant="subtitle2" noWrap>
                                  {row?.phone_number}
                                </OverflowTypography>
                              </TableCell>
                              <TableCell>
                                <OverflowTypography variant="subtitle2" noWrap>
                                  {row?.email}
                                </OverflowTypography>
                              </TableCell>
                              {/* <TableCell>
                                <Label color='success' >
                                  {row?.api_key}
                                </Label>
                              </TableCell> */}
                              <TableCell>
                                <Stack direction="row" spacing={2}>
                                  <Tooltip title="Edit" arrow>
                                    <LoadingButton
                                      onClick={() => handleEditClick(row)}
                                      loading={false}
                                      variant="contained"
                                      disabled={selectedData?.phone_number === row?.phone_number}
                                    >
                                      Edit
                                    </LoadingButton>
                                  </Tooltip>

                                  <Tooltip title="Edit" arrow>
                                    <LoadingButton
                                      onClick={() => setDeleteData(row)}
                                      loading={false}
                                      variant="contained"
                                      color='error'
                                      disabled={selectedData?.phone_number === row?.phone_number}
                                    >
                                      Delete
                                    </LoadingButton>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    ) : (
                      <TableFooter>
                        <TableRow>
                          <TableCell style={{ width: '100%' }} align="center" colSpan={6} v>
                            <Typography mt={2}>No user found.</Typography>
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </Scrollbar>
              )}
            </Box>
          </Box>
        </Card>
      </Container>
      {selectedData && <Dialog
        fullWidth
        maxWidth="md"
        scroll='body'
        disableEscapeKeyDown={() => setSelectedData(null)}
        open={selectedData ? true : false}
        onClose={() => setSelectedData(null)}
        transitionDuration={{
          enter: theme.transitions.duration.shortest,
          exit: theme.transitions.duration.shortest - 80,
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>{selectedData?.phone_number ? `Update center details` : `Add new center`}</span>
          <Label><Link
            onClick={() => setSelectedData(null)}
            component="button"
            color='black'
            underline='none'
            variant="button">
            Close
          </Link></Label>
        </DialogTitle>
        <Box px={3} pb={3}>
          <FormProvider methods={methods} onSubmit={onSubmit}>
            <Grid container spacing={2} justifyContent='center'>

              <Grid item md={4} sm={6} xs={8}>
                <Controller
                  name="country_code"
                  control={control}
                  render={({ field }) => (
                    <RHFAutocomplete
                      {...field}
                      label="Country"
                      value={field?.value || (defaultValues?.country_code || '91')}
                      options={countries.map((country) => `${country.label}/${country.phone}`)}
                      getOptionLabel={(option) => option}
                      isOptionEqualToValue={(option, value) =>
                        option?.split('/')[0] === value || option?.split('/')[1] === value
                      }
                      renderOption={(props, option) => {
                        const { code, label, phone } = countries.find(
                          (country) => country.label === option?.split('/')[0]
                        ) || {};

                        if (!label) return null;

                        return (
                          <li {...props} key={label}>
                            <Iconify
                              icon={`circle-flags:${code?.toLowerCase()}`}
                              width={28}
                              sx={{ mr: 1 }}
                            />
                            {label} ({code}) +{phone}
                          </li>
                        );
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item md={4} sm={6} xs={8}>
                <Controller
                  name="phone_number"
                  control={control}
                  rules={{
                    required: "Please enter the phone number.",
                    pattern: {
                      value: /^[0-9]{10}$/i,
                      message: "Invalid phone number",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Phone Number"
                      variant="outlined"
                      fullWidth
                      sx={{ mt: '0px !important' }}
                      placeholder="9999999999"
                      margin="normal"
                      error={!!errors.phone_number}
                      helperText={errors.phone_number?.message}
                      onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
                    />
                  )}
                />
              </Grid>

              <Grid item md={4} sm={6} xs={8}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      sx={{ minWidth: 70 }}
                      label="Center Name"
                      fullWidth
                      {...register('name', {
                        required: "Please enter business center name."
                      })}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item md={6} sm={6} xs={12}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      sx={{ minWidth: 70 }}
                      label="Email"
                      fullWidth
                      {...register('email', {
                        required: 'Enter the email id',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item md={6} sm={6} xs={12}>
                <Controller
                  name="billing_address"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      sx={{ minWidth: 70 }}
                      label="City"
                      fullWidth
                      {...register('billing_address', {
                        required: 'Please enter the billing address.'
                      })}
                      error={!!errors.billing_address}
                      helperText={errors.billing_address?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item md={6} sm={6} xs={12}>
                <Controller
                  name="state"
                  control={control}
                  defaultValue={defaultValues?.state || ""}
                  rules={{
                    required: 'Please select or enter a state.'
                  }}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      freeSolo
                      id="free-solo-2-demo"
                      disableClearable
                      options={indianStates}
                      onChange={(event, value) => {
                        field.onChange(value || event.target.value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select State"
                          error={!!errors.state}
                          helperText={errors.state?.message}
                          inputProps={{
                            ...params.inputProps,
                            type: 'text',
                          }}
                          onChange={(event) => {
                            field.onChange(event.target.value);
                          }}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item md={6} sm={6} xs={12}>
                <Controller
                  name="pin"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      sx={{ minWidth: 100 }}
                      label="Pin code"
                      fullWidth
                      {...register('pin', {
                        required: 'Please enter the pin code.',
                        pattern: {
                          value: /^[0-9]{6,6}$/i,
                          message: 'Invalid pin code'
                        }
                      })}
                      error={!!errors.pin}
                      helperText={errors.pin?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item md={0} sm={0} xs={0} sx={{ display: 'none' }}>
                <Controller
                  name="userType"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      defaultValue={"center"}
                    />
                  )}
                />
              </Grid>


            </Grid>
            <Grid item xs={12} textAlign='center'>
              <Divider sx={{ my: 2 }} />
              <LoadingButton
                sx={{ minWidth: 150 }}
                type="submit"
                variant="contained"
                loading={isSubmitting}
              >
                Save
              </LoadingButton>
            </Grid>
          </FormProvider>
        </Box>


      </Dialog>
      }

      {deleteData && <Dialog
        fullWidth
        maxWidth="xs"
        scroll='body'
        disableEscapeKeyDown={() => setDeleteData(null)}
        open={deleteData ? true : false}
        onClose={() => setDeleteData(null)}
        transitionDuration={{
          enter: theme.transitions.duration.shortest,
          exit: theme.transitions.duration.shortest - 80,
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          Confirmation
        </DialogTitle>
        <Box sx={{ px: 3, pb: 4 }}>Do you want to delete center <b><u>{deleteData?.business_name}</u></b>?</Box>
        <Divider />
        <DialogActions>
          <Stack direction='row' spacing={4} sx={{ px: 2, py: 1 }}>
            <Button variant="outlined" color="error" onClick={() => setDeleteData(null)}>
              Cancel
            </Button>

            <LoadingButton
              type="submit"
              variant="contained"
              loading={loading}
              onClick={onDeleteDataClick(deleteData)}
            >
              Confirm
            </LoadingButton>
          </Stack>
        </DialogActions>
      </Dialog>}
    </>
  );
}
